<template>
    <div class="bg-gray-100 d-flex flex-column h-100">

        <div v-if="isEdit" key="snippetHeaderEdit" class="px-3 pt-3 pb-2 d-flex justify-content-between">
            <b-form-group
                id="fieldset-snippet-name"
                label="Snippet Name"
                label-for="snippet-name-input"
                class="flex-grow-1"
                :state="snippetNameFieldState"
            >
                <template #label>
                    <b>Snippet Name</b>
                    <span class="ml-3 text-muted small">Required / Must be unique for shared snippets</span>
                </template>
                <b-form-input 
                    id="snippet-name-input" 
                    v-model="localSnippet.name"
                    autofocus
                    :state="snippetNameFieldState"
                    :disabled="disabled"
                    @input="checkSave"
                ></b-form-input>
            </b-form-group>
            <b-form-group
                id="fieldset-snippet-shared"
                label="Shared with entire company?"
                label-for="snippet-shared-checkbox"
                class="ml-4"
            >
                <template #label>
                    <b>Shared company-wide?</b>
                </template>
                <b-form-checkbox 
                    v-model="localSnippet.is_shared" 
                    size="lg" 
                    name="snippet-shared-checkbox" 
                    switch 
                    class="text-center"
                    :disabled="!canManageShared || disabled"
                    @input="checkSaveNow"
                >
                    <span class="d-inline-block special-text font-size-90 text-muted" style="min-width: 30px;">{{ localSnippet.is_shared ? 'Yes' : 'No' }}</span>
                </b-form-checkbox>
            </b-form-group>
            <div class="ml-4 align-self-center pt-2">
                <b-button 
                    v-b-tooltip.hover.top="'Delete'" 
                    variant="subtle-danger" 
                    :disabled="disabled"
                    @click="requestDelete"
                >
                    <b-icon icon="trash"></b-icon>
                </b-button>
            </div>
        </div>

        <quill-editor-full 
            ref="editor"
            v-if="isEdit && supportsHtml"
            v-model="localSnippet.content" 
            :disabled="loading || disabled || isInsert" 
            :placeholder="loading ? 'Loading...' : placeholder" 
            class="border-top border-bottom overflow-hidden w-100" 
            :content-height="isInsert ? '560px' : '510px'"
            toolbar="standard"
            :hide-toolbar="isInsert"
            :loading="loading"
            @input="checkSave"
        >
        </quill-editor-full>

        <div v-if="isEdit && !supportsHtml" class="flex-fill">
            <b-form-textarea
                id="snippet-editor-textarea"
                v-model="localSnippet.content"
                :placeholder="loading ? 'Loading...' : placeholder" 
                :disabled="loading || disabled" 
                rows="25"
                max-rows="26"
                class=""
                @input="checkSave"
            ></b-form-textarea>
        </div>

        <div v-if="isEdit" class="py-2 px-3 line-height-100 flex-fill text-right" style="">
            <transition name="fade-fast" mode="out-in">
                <span v-if="localSnippet.content && !localSnippet.name" class="mr-3 font-size-70 special-text text-danger">Snippet Name is Required</span>
            </transition>
            <transition name="fade-fast" mode="out-in">
                <span v-if="hasUnsavedChanges" key="hasUnsavedChanges" class="special-text font-size-70 text-muted">Unsaved</span>
                <span v-else key="allSaved" class="special-text font-size-70 text-success">Saved</span>
            </transition>
        </div>

        <!-- INSERT MODE STUFF -->

        <div v-if="isInsert" key="snippetHeaderInsert" class="px-3 pt-3 pb-2 d-flex justify-content-between">
            <div class="">
                <div class="hot-edit-label mb-1">Snippet Name</div>
                <h4>{{ localSnippet.name }}</h4>
            </div>
        </div>

        <div v-if="isInsert" class="border-top border-bottom bg-white px-3 py-3 flex-fill overflow-auto">
            <!-- put all the insert stuff here -->
            <div v-if="supportsHtml" class="quill-content" v-html="localSnippet.content"></div>
            <div v-else class="text-show-breaks">{{ localSnippet.content }}</div>
        </div>

        <div v-if="isInsert" class="py-2 px-3 text-right">
            <b-button variant="primary" size="lg" @click="handleInsert">Insert &rarr;</b-button>
        </div>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

export default {
    name: 'SnippetEditor',
    components: {
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        snippet: {  // can be partial object, we will fetch here - has '_localId' for local tracking, 
            // might not have 'id' - if not, then its a new snippet just created by parent 
            // NOT reactive to changes here... we fetch things locally, then update parent, but don't react to changes
            type: Object,
            required: true,
        },
        mode: {  // options are 'insert' and 'edit'  
            type: String,
            default: 'insert',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Write the snippet here...',
        },
        supportsHtml: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            saving: false,
            localSnippet: {},  // local values, WITHOUT content, in case prop only had name/id/_localId
            lastSaved: {},  // entire object of last saved... including content - keep empty object if never saved
            content: '',  // local content, not in localSnippet
            contentLastSaved: null, 
        }
    },
    computed: {
        anyLoading() {
            return this.loading || this.saving;
        },
        isNew() {
            return !this.localSnippet.id;
        },
        isSaved() {
            // is this snippet in the DB?
            return !!this.localSnippet.id;
        },
        canManageShared() {
            // can the current user share / manage shared / create shared snippets?
            return this.$currentUserInfo.is_tenant_admin;
        },
        snippetNameFieldState() {
            // return false if no name, or null if OK (so we don't show green checkmark if filled in... which could be a false positive if its a duplicate)
            if (this.loading) {
                return null;  // don't show anything when loading...
            }
            return !this.localSnippet.name ? false : null;
        },
        hasRequiredFields() {
            // a simple validation thing for now
            // must have 'name' (and must be unique, but server tells us that)
            return !!this.localSnippet.name;
        },
        hasUnsavedChanges() {
            return this.isNew || this.isAnyChanged();
        },
        isEdit() {
            return this.mode === 'edit';
        },
        isInsert() {
            return this.mode === 'insert';
        },
    },
    created() {
        //const { content, ...otherSnippetData } = this.snippet;
        this.localSnippet = cloneDeep(this.snippet);
        //this.content = content ?? '';  // set both content and contentLastSaved here 
        if (this.localSnippet.id) {
            // does it have an id?  then it was saved at some point
            this.setLastSaved(this.snippet);
        }
        //this.contentLastSaved = content ?? '';
        // make debounced save method for later...
        //this.debouncedSave = debounce(this.saveSnippet, 1000);
        this.debouncedCheckSave = debounce(this._checkSave, 1000);
    },
    /* beforeDestroy() {
        // cancel the debounced save function?  actually... maybe not needed... we had infinite loop, but root cause was caused by backend trimming strings and frontend not
        // but it might still be a good idea to do this.... 
        console.log('IN beforeDestroy - cancelling debounced function');
        if (this.debouncedCheckSave) {  // not sure if we need to check if it exists first?
            this.debouncedCheckSave.cancel();
        }
    }, */
    mounted() {
        if (this.localSnippet.id) {
            // if it has id, then it is existing, fetch it to get ALL fields, not just partial ones from parent 
            this.getSnippet();
        } else {
            // otherwise its a new snippet - no need to stash last saved data... would have happend in created
        }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getSnippet() {
            this.loading = true;
            let resp;
            const snippetId = this.localSnippet.id;
            if (!snippetId) {
                console.log('No snippet ID so cannot getSnippet - maybe new snippet from parent');
                return;
            }
            try {
                resp = await suppliosApi.snippet.retrieve(snippetId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.setDataFromApi(resp.data);
            }
            this.loading = false;
        },
        async saveSnippet() {
            if (this.disabled) {
                return;
            }
            this.saving = true;
            let resp;
            // MERGE DATA FOR UPDATE 

            const snippetId = this.localSnippet.id;
            const isUpdate = !!snippetId;  // is it update or create?
            // 
            const snippetData = cloneDeep(this.localSnippet);  
            try {
                if (isUpdate) {
                    resp = await suppliosApi.snippet.update(snippetId, snippetData);
                } else {
                    resp = await suppliosApi.snippet.create(snippetData);
                }
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.setDataFromApi(resp.data);
            }
            this.saving = false;
        },
        setDataFromApi(apiData) {
            //const { content, ...otherSnippetData } = apiData;
            const newData = cloneDeep(apiData);  
            // we don't DIRECTLY set on this.localData, in case user is still typing
            // so IF WE HAVE localSnippet.content (might not, if parent doesn't have it, could be undefined) we overwrite with local 'content', then call checkSave() just in case the user stopped typing in between the save calls
            const localContent = this.localSnippet.content;
            if (localContent !== undefined && typeof localContent === 'string') {
                // checking this is VERY important, or else you could delete all the existing local content!
                newData.content = this.localSnippet.content;
            }
            // now MERGE (assign is OK here) with this.localSnippet - because we need to keep _localId and _position which are local things only, won't be returned from backend
            this.localSnippet = Object.assign({}, this.localSnippet, newData);
            // DO NOT SET THIS HERE - this.content = content ?? '';
            // this.contentLastSaved = content;
            this.setLastSaved(apiData);  // this clones it too
            this.updateParent();
            this.checkSave();  // call this just in case user was typing more on save, but then stopped before response came back
        },
        updateParent() {  // make sure this.localSnippet is updated first 
            // don't pass content up to parent - just other stuff - but need _localId so parent knows which one we are
            const updatedSnippet = cloneDeep(this.localSnippet);
            delete updatedSnippet.content;  // remove content 
            this.$emit('update', updatedSnippet);
        },
        /* handleContentInput(content) {
            // from @input of the editor 
            if (!this.hasRequiredFields) {
                return;  
            }
            const isChanged = this.isContentChanged(content);
            if (isChanged) {
                this.debouncedSave();
            }
        },
        isContentChanged(newContent) {
            return newContent !== this.lastSaved.content;
        }, */
        isAnyChanged() {
            // NOTE - the backend will TRIM text values, so when you compare, you MUST trim strings
            const current = this.extractData(this.localSnippet);
            const lastSaved = this.extractData(this.lastSaved);
            return JSON.stringify(current) !== JSON.stringify(lastSaved);
        },
        extractData(snippetData) {
            // common helper to extract ONLY the things we want to use for diffing
            // so we use the same thing for comparing current data to last saved
            const { content, name, is_shared, id } = snippetData;
            // return { content, name, is_shared, id };
            // NOTE - the backend will TRIM text values, so when you compare, you MUST trim strings
            //      - OR, don't have backend trim things - in DRF use CharField(trim_whitespace=False) - use in Meta.extra_kwargs on serializer 
            const rawData = { content, name, is_shared, id };  // make a copy only with the keys we want to compare 
            const trimKeys = ['name'];  // the keys that the backend will trim - we added trim_whitespace=False for 'content' so we honor extra newlines, etc. 
            const ret = {};
            Object.entries(rawData).forEach(([key, rawVal]) => {
                if (typeof rawVal === 'string' && trimKeys.includes(key)) {
                    ret[key] = rawVal.trim();
                } else {
                    ret[key] = rawVal;
                }
            });
            return ret;
        },
        setLastSaved(snippetData) {
            // pass in ALL things for the snippet (including content)
            // but we want to ignore extraneous stuff that we don't change here, and that might be passed from parent (_localId)
            // so we destructure and pull out only the things we want
            this.lastSaved = cloneDeep(snippetData);
        },
        _checkSave() {
            // check save, if anything changed, call save
            // wrap this with debounced and immediate methods and call those, not this one
            // this.checkSave() is the debounced version 
            if (!this.hasRequiredFields) {
                return;  
            }
            if (this.isAnyChanged()) {
                console.log('CHANGED - WILL SAVE');
                this.saveSnippet();
            } else {
                console.log('NOT CHANGED - NO SAVE');
            }
        },
        checkSave() {
            this.debouncedCheckSave();  // wraps _checkSave() - set in created()
        },
        checkSaveNow() {
            // immediately call _checkSave - not debounced
            this._checkSave();
        },
        requestDelete() {
            this.$emit('request-delete');
        },
        handleInsert() {
            // only for INSERT mode
            if (!this.isInsert) {
                return;
            }
            this.$emit('insert', this.localSnippet.content);  // emit the content to be inserted... parent will relay up to whatever wraps this
        }
    }
}
</script>

<style>

#snippet-editor-textarea {
    border-color: transparent;
}

</style>